import { gql, useQuery } from '@provision/graphql-client';
import { useEffect } from 'react';
import { isNullish } from 'remeda';
import { useGlobalStore } from 'src/store';

const GET_STRIPE_ACCOUNT_ONBOARD_STATUS = gql`
  query GetStripeAccountOnboardStatus($params: GetStripeAccountOnboardStatusInput!) {
    getStripeAccountOnboardStatus(params: $params) {
      detailsSubmitted
      chargesEnabled
      country
      email
      bankAccount {
        bankName
        last4
        status
      }
      payoutSchedule
      hasAccount
    }
  }
`;

interface GetStripeAccountOnboardStatusResponse {
  getStripeAccountOnboardStatus: {
    detailsSubmitted: boolean;
    chargesEnabled?: boolean;
    country?: string;
    email?: string;
    bankAccount?: {
      bankName?: string;
      last4?: string;
      status?: string;
    };
    payoutSchedule?: string;
    hasAccount: boolean;
  };
}

interface GetStripeAccountOnboardStatusVariables {
  params: {
    partnerId: number;
  };
}

export const useStripeAccountOnboardStatus = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { data, loading, error, refetch } = useQuery<
    GetStripeAccountOnboardStatusResponse,
    GetStripeAccountOnboardStatusVariables
  >(GET_STRIPE_ACCOUNT_ONBOARD_STATUS, {
    variables: {
      params: {
        partnerId: selectedPartnerId ?? 0,
      },
    },
    skip: isNullish(selectedPartnerId),
    fetchPolicy: 'network-only',
  });

  // Refetch status when window regains focus (e.g., user returns from Stripe onboarding)
  useEffect(() => {
    const handleFocus = () => {
      void refetch();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetch]);

  return {
    detailsSubmitted: data?.getStripeAccountOnboardStatus?.detailsSubmitted ?? false,
    chargesEnabled: data?.getStripeAccountOnboardStatus?.chargesEnabled ?? false,
    country: data?.getStripeAccountOnboardStatus?.country,
    email: data?.getStripeAccountOnboardStatus?.email,
    bankAccount: data?.getStripeAccountOnboardStatus?.bankAccount,
    payoutSchedule: data?.getStripeAccountOnboardStatus?.payoutSchedule,
    hasAccount: data?.getStripeAccountOnboardStatus?.hasAccount ?? false,
    loading,
    error,
    refetch,
  };
};
